import { useEffect } from "react"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { HelmetProvider } from "react-helmet-async"
import { Layout } from "@/components/layout"
import { UpdateBanner } from "@/components/UpdateBanner"
import { useVersionCheck } from "@/hooks/useVersionCheck"
import { initTagCache } from "@/services/tagCacheService"
import { initStreamIndex } from "@/services/streamSearchService"
import { FEATURES } from "@/config/features"
import {
  LandingPage,
  VanillaHomePage,
  MinePage,
  StreamPage,
  PublicStreamPage,
  ExplorePage,
  SwapPage,
  StakePage,
  BridgePage,
  StatsPage,
  DocsPage,
  StaticDocsPage,
  BlogPage,
  BlogPostPage,
  StaticBlogPage,
  DeckListPage,
  DeckPage,
  TermsPage,
  PrivacyPage,
  NotFoundPage,
  DirectoryPage,
  MiningOverviewPage,
  StreamStatsPage,
  SpreadPage,
  TrendingPage,
} from "@/pages"

function App() {
  const { updateAvailable, refresh } = useVersionCheck()

  // Initialize caches in background on app startup (streams-only services)
  useEffect(() => {
    if (FEATURES.streams) { initTagCache(); initStreamIndex() }
  }, [])

  return (
    <HelmetProvider>
      {updateAvailable && <UpdateBanner onRefresh={refresh} />}
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={FEATURES.vanillaHome ? <VanillaHomePage /> : <LandingPage />} />
            {FEATURES.mining ? (
              <>
                <Route path="/mining" element={<MiningOverviewPage />} />
                <Route path="/mine" element={<MinePage />} />
                <Route path="/explore" element={<ExplorePage />} />
              </>
            ) : (
              ["/mining", "/mine", "/explore"].map((path) => (
                <Route key={path} path={path} element={<Navigate to="/" replace />} />
              ))
            )}
            {FEATURES.streams ? (
              <>
                <Route path="/s/:identifier" element={<PublicStreamPage />} />
                <Route path="/stream" element={<StreamPage />} />
                <Route path="/stream/:referrer" element={<StreamPage />} />
                <Route path="/trending" element={<TrendingPage />} />
                <Route path="/trending/tag/:tag" element={<TrendingPage />} />
                <Route path="/streams" element={<DirectoryPage />} />
                <Route path="/streams/:category" element={<DirectoryPage />} />
                <Route path="/stream-stats" element={<StreamStatsPage />} />
              </>
            ) : (
              [
                "/s/:identifier",
                "/stream",
                "/stream/:referrer",
                "/trending",
                "/trending/tag/:tag",
                "/streams",
                "/streams/:category",
                "/stream-stats",
              ].map((path) => (
                <Route key={path} path={path} element={<Navigate to="/" replace />} />
              ))
            )}
            <Route path="/swap" element={<SwapPage />} />
            <Route path="/stake" element={<StakePage />} />
            <Route path="/bridge" element={<BridgePage />} />
            <Route path="/stats" element={<StatsPage />} />
            <Route path="/spread" element={<SpreadPage />} />
            <Route path="/docs" element={<Navigate to="/docs/overview" replace />} />
            <Route path="/docs/:slug" element={<DocsPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPostPage />} />
            <Route path="/about" element={<Navigate to="/docs/overview" replace />} />
            <Route path="/decks" element={<DeckListPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Route>
          {/* Deck presenter - fullscreen, no layout */}
          <Route path="/decks/:slug" element={<DeckPage />} />
          {/* Static pages for SEO - rendered without layout for prerendering */}
          <Route path="/static/docs/:slug" element={<StaticDocsPage />} />
          <Route path="/static/blog/:slug" element={<StaticBlogPage />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  )
}

export default App
